import React, { useContext, PropsWithChildren } from 'react';
import { useEventCustomFieldDefinitions } from 'queries/events/customfields/useEventCustomFieldDefinitions';
import { useDeleteEventCustomFieldDefinition } from 'queries/events/customfields/useDeleteEventCustomFieldDefinition';
import { useUpdateEventCustomFieldDefinition } from 'queries/events/customfields/useUpdateEventCustomFieldDefinition';
import { EventCustomFieldDefinitionsContext } from './EventCustomFieldDefinitionsContext';
import { ErrorPage } from 'components/ErrorPage';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import { useCurrentSchoolyearIdSchoolId } from 'queries/schoolyears/atom';
import { useCreateEventCustomFieldDefinition } from 'queries/events/customfields/useCreateEventCustomFieldDefinition';
import { Navigate } from 'react-router-dom';

export function EventCustomFieldDefinitionsProvider({
  children,
}: PropsWithChildren<{}>) {
  const { currentSchoolyearId, currentSchoolId, reset } =
    useCurrentSchoolyearIdSchoolId();

  // force type because we know it is defined
  const schoolId = currentSchoolId as string;
  const schoolyearId = currentSchoolyearId as string;

  const {
    data: eventCustomFieldDefinitions = [],
    isLoading,
    isError,
    error,
  } = useEventCustomFieldDefinitions({
    schoolId,
    schoolyearId,
  });

  const deleteDefinitionMutation = useDeleteEventCustomFieldDefinition({
    schoolId,
    schoolyearId,
  });

  const updateDefinitionMutation = useUpdateEventCustomFieldDefinition({
    schoolId,
    schoolyearId,
  });

  const createDefinitionMutation = useCreateEventCustomFieldDefinition({
    schoolId,
    schoolyearId,
  });

  if (error?.response?.status === 404) {
    // in case the user accesses a deleted school (outdated bookmark), we should redirect to the root path
    reset();
    return <Navigate to="/" />;
  }

  if (isLoading) {
    return <SpinnerComponent />;
  }
  if (isError) {
    return <ErrorPage />;
  }

  return (
    <EventCustomFieldDefinitionsContext.Provider
      value={{
        definitions: eventCustomFieldDefinitions,
        deleteDefinition: deleteDefinitionMutation.mutateAsync,
        updateDefinition: updateDefinitionMutation.mutateAsync,
        createDefinition: createDefinitionMutation.mutateAsync,
      }}
    >
      {children}
    </EventCustomFieldDefinitionsContext.Provider>
  );
}

export function useEventCustomFieldDefinitionsContext() {
  const context = useContext(EventCustomFieldDefinitionsContext);

  if (context === undefined) {
    throw new Error(
      'useEventCustomFieldDefinitionsContext must be used within an EventCustomFieldDefinitionsProvider',
    );
  }

  return context;
}
